import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Phone, Mail, CodeXml } from 'lucide-react';
import { scrollToSection } from '../utils/scrollUtils';

const Header = ({ isMenuOpen, setIsMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const isLandingPage = location.pathname === '/';
  const isForCompaniesPage = location.pathname === '/for-companies';

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (isForCompaniesPage) return;

    const handleScroll = () => {
      if (ticking.current) return;

      ticking.current = true;
      requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const scrollDiff = currentScrollY - lastScrollY.current;

        if (currentScrollY < 50) {
          setIsVisible(true);
          lastScrollY.current = currentScrollY;
        } else if (scrollDiff > 10) {
          setIsVisible(false);
          lastScrollY.current = currentScrollY;
        } else if (scrollDiff < -10) {
          setIsVisible(true);
          lastScrollY.current = currentScrollY;
        }

        ticking.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isForCompaniesPage]);

  const handleNavigation = (item) => {
    setIsMenuOpen(false);

    if (item === 'HOME') {
      navigate('/');
    } else if (item === 'BLOG') {
      navigate('/blog');
    } else if (item === 'FOR COMPANIES') {
      navigate('/for-companies');
    } else if (item === 'PROTECTION') {
      navigate('/software-protection');
    } else if (item === 'CASE STUDIES') {
      navigate('/case-studies');
    } else if (item === 'CONTACT') {
      navigate('/contact');
    } else if (isLandingPage) {
      scrollToSection(item.toLowerCase());
    } else {
      const section = item.toLowerCase();
      navigate(`/#${section}`);
      setTimeout(() => {
        scrollToSection(section);
      }, 100);
    }
  };

  const handleLogoClick = () => {
    setIsMenuOpen(false);
    navigate('/');
  };

  const isActive = (item) => {
    const path = location.pathname;
    if (item === 'HOME') return path === '/';
    if (item === 'BLOG') return path.startsWith('/blog');
    if (item === 'FOR COMPANIES') return path === '/for-companies';
    if (item === 'PROTECTION') return path === '/software-protection';
    if (item === 'CASE STUDIES') {
      return path === '/case-studies'
        || path.startsWith('/case-studies/')
        || /^\/(en|sk)\/case-studies(\/|$)/.test(path);
    }
    if (item === 'CONTACT') return path === '/contact';
    return false;
  };

  const shouldShow = isVisible || isMenuOpen;

  const headerClasses = isForCompaniesPage
    ? "static-header"
    : `fixed-header ${shouldShow ? 'header-visible' : 'header-hidden'}`;

  const navItems = [
    { label: 'HOME', action: () => handleNavigation('HOME') },
    { label: 'FOR COMPANIES', action: () => handleNavigation('FOR COMPANIES') },
    { label: 'PROTECTION', action: () => handleNavigation('PROTECTION') },
    { label: 'CASE STUDIES', action: () => handleNavigation('CASE STUDIES') },
    { label: 'BLOG', action: () => handleNavigation('BLOG') },
    { label: 'CONTACT', action: () => handleNavigation('CONTACT') },
    { label: 'DISCORD', action: () => { setIsMenuOpen(false); window.open('https://discord.gg/PfTTpSVCVZ', '_blank'); }, external: true },
  ];

  return (
    <>
      <nav className={headerClasses}>
        <div className="px-4 py-2 md:px-6 md:py-4">
          <div className="flex justify-between items-center">
            <div
              className="flex items-center space-x-2 md:space-x-3 cursor-pointer relative z-[60]"
              onClick={handleLogoClick}
            >
              <div className="w-8 h-8 md:w-9 md:h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <CodeXml className="w-4 h-4 md:w-5 md:h-5 text-white/90" strokeWidth={2} aria-hidden="true" />
              </div>
              <span className="text-lg md:text-xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                TukeWIN
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-3 lg:gap-6">
              {['HOME', 'FOR COMPANIES', 'PROTECTION', 'CASE STUDIES', 'BLOG', 'CONTACT'].map((item) => (
                <button
                  key={item}
                  onClick={() => handleNavigation(item)}
                  className={`text-slate-300 hover:text-white transition-colors text-xs lg:text-sm font-medium link-underline font-mono uppercase bg-transparent border-none cursor-pointer ${isActive(item) ? 'link-underline-active' : ''}`}
                >
                  {item}
                </button>
              ))}
              <a
                href="tel:+421952175301"
                className="flex items-center gap-2 text-slate-300 hover:text-indigo-400 transition-colors text-sm font-medium font-mono whitespace-nowrap"
                aria-label="Call us"
                title="+421 952 175 301"
              >
                <Phone className="w-4 h-4" />
                <span>+421 952 175 301</span>
              </a>
              <a
                href="mailto:administrator@tuke.win"
                className="text-slate-300 hover:text-indigo-400 transition-colors group relative"
                aria-label="Email us"
                title="administrator@tuke.win"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden relative z-[60] w-11 h-11 flex items-center justify-center touch-manipulation"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <div className="hamburger-icon">
                <span className={`hamburger-line ${isMenuOpen ? 'hamburger-line-1-active' : ''}`} />
                <span className={`hamburger-line ${isMenuOpen ? 'hamburger-line-2-active' : ''}`} />
                <span className={`hamburger-line ${isMenuOpen ? 'hamburger-line-3-active' : ''}`} />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-menu-overlay ${isMenuOpen ? 'mobile-menu-overlay-active' : ''}`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div className={`mobile-menu-panel ${isMenuOpen ? 'mobile-menu-panel-active' : ''}`}>
        <div className="mobile-menu-content">
          <nav className="mobile-menu-nav">
            {navItems.map((item, index) => (
              <button
                key={item.label}
                onClick={item.action}
                className={`mobile-menu-item ${isActive(item.label) ? 'mobile-menu-item-active' : ''}`}
                style={{ transitionDelay: isMenuOpen ? `${index * 50 + 100}ms` : '0ms' }}
              >
                <span className="mobile-menu-item-text">{item.label}</span>
                {item.external && (
                  <svg className="w-5 h-5 ml-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                )}
              </button>
            ))}
          </nav>

          {/* Contact Links */}
          <div
            className="flex flex-col gap-3 mt-4 pt-4 border-t border-slate-700/50"
            style={{ transitionDelay: isMenuOpen ? `${navItems.length * 50 + 100}ms` : '0ms' }}
          >
            <a
              href="tel:+421952175301"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/50 transition-all"
              onClick={() => setIsMenuOpen(false)}
            >
              <Phone className="w-5 h-5 text-indigo-400" />
              <span className="font-medium">+421 952 175 301</span>
            </a>
            <a
              href="mailto:administrator@tuke.win"
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/50 transition-all"
              onClick={() => setIsMenuOpen(false)}
            >
              <Mail className="w-5 h-5 text-indigo-400" />
              <span className="font-medium">administrator@tuke.win</span>
            </a>
          </div>

          {/* CTA Button */}
          <button
            onClick={() => {
              setIsMenuOpen(false);
              navigate('/contact');
            }}
            className="mobile-menu-cta"
            style={{ transitionDelay: isMenuOpen ? `${navItems.length * 50 + 200}ms` : '0ms' }}
          >
            GET STARTED
          </button>
        </div>
      </div>
    </>
  );
};

export default Header;
